import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Wrench,
  MessageCircle,
  BadgeCheck,
  Wallet,
  Truck,
  Search,
  ClipboardCheck,
  Sparkles,
  Handshake,
  Repeat2,
  MapPin,
  Phone,
} from "lucide-react";
import Seo from "@/components/site/Seo";

const VALUES = [
  {
    icon: BadgeCheck,
    text: "Transparență",
    sub: "Informații complete și corecte despre fiecare autoturism.",
  },
  {
    icon: Wrench,
    text: "Verificare tehnică",
    sub: "Fiecare mașină este verificată înainte să ajungă la tine.",
  },
  {
    icon: MessageCircle,
    text: "Consultanță",
    sub: "Te ghidăm la fiecare pas, fără presiune și fără grabă.",
  },
  {
    icon: Wallet,
    text: "Finanțare",
    sub: "Prin parteneri autorizați — TBI Bank, BT Direct, UniCredit, Mogo.",
  },
  {
    icon: Repeat2,
    text: "Buy Back",
    sub: "Mașina ta la schimb.",
  },
  {
    icon: Truck,
    text: "Livrare în țară",
    sub: "Aducem mașina la tine, oriunde în România.",
  },
];

const PROCESS = [
  {
    n: "01",
    icon: Search,
    title: "Selecție atentă",
    desc: "Alegem doar autoturisme care merită să poarte semnătura AutoRIO.",
  },
  {
    n: "02",
    icon: ClipboardCheck,
    title: "Verificare tehnică",
    desc: "Analiză completă a stării mecanice, electronice și de caroserie.",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Pregătire estetică",
    desc: "Fiecare mașină este pregătită înainte de a fi listată în stoc.",
  },
  {
    n: "04",
    icon: Handshake,
    title: "Predare și livrare",
    desc: "Consultanță pentru înmatriculare, livrare oriunde în țară.",
  },
];

const REASONS = [
  { icon: ShieldCheck, text: "Garanție inclusă", sub: "prelungibilă până la 3 ani" },
  { icon: Wallet, text: "Finanțare prin parteneri autorizați", sub: "aprobare online rapidă" },
  { icon: Repeat2, text: "Serviciu Buy Back", sub: "mașina ta la schimb" },
  { icon: Wrench, text: "Verificare tehnică efectuată", sub: "documente în regulă" },
  { icon: Truck, text: "Livrare în toată țara", sub: "acasă la tine" },
  { icon: MessageCircle, text: "Consiliere pentru înmatriculare", sub: "gratuit, fără drumuri" },
];

export default function About() {
  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-20">
      <Seo
        title="Despre AutoRIO · Parc auto premium în Suceava"
        description="AutoRIO este un dealer auto din Suceava specializat în autoturisme atent selectate, verificate tehnic și pregătite pentru livrare oriunde în România."
        path="/despre"
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-14 sm:pt-40 sm:pb-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 60%)",
          }}
        />
        <div className="relative container-wide text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.05] backdrop-blur-md px-4 py-1.5 border border-white/[0.1]">
              <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/80">
                Parc auto · Suceava
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.02] tracking-tight">
              Despre{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                AutoRIO
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl mx-auto">
              Parc auto din Suceava specializat în autoturisme atent selectate,
              verificate și pregătite pentru livrare.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY / STORY (evergreen — no fabricated numbers or dates) */}
      <section className="container-wide py-14 sm:py-20 border-t border-white/[0.06]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow">Ce ne definește</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Un dealer diferit, prin{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                principii
              </span>
              .
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/70 text-base sm:text-lg leading-[1.85]">
            <p>
              AutoRIO este un parc auto din Suceava care alege atent fiecare
              autoturism pe care îl aduce în stoc. Nu ne grăbim să vindem orice
              mașină — construim relații cu clienți care apreciază{" "}
              <span className="text-white font-semibold">
                lucrurile făcute corect
              </span>
              .
            </p>
            <p>
              Fiecare mașină trece printr-un proces de{" "}
              <span className="text-[#D4AF37] font-semibold">
                verificare tehnică
              </span>{" "}
              și pregătire înainte de a fi listată. Prezentăm exact starea
              vehiculului, fără să ascundem lucruri care contează pentru
              cumpărător.
            </p>
            <p>
              Suntem alături de tine și după vânzare: garanție,{" "}
              <span className="text-white font-semibold">
                consultanță pentru înmatriculare
              </span>
              , finanțare prin parteneri autorizați, serviciu Buy Back și
              livrare oriunde în țară.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="container-wide py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow">Valorile AutoRIO</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
            Ce aducem{" "}
            <span className="font-serif-italic gold-gradient-text font-medium">
              în plus
            </span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.text}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="rounded-2xl border border-white/[0.06] bg-[#111111] p-6 flex items-start gap-4 hover:border-[#D4AF37]/30 transition-colors"
            >
              <span className="h-11 w-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                <v.icon
                  className="h-5 w-5 text-[#D4AF37]"
                  strokeWidth={2.2}
                />
              </span>
              <div>
                <div className="text-white font-bold text-[16px] leading-tight">
                  {v.text}
                </div>
                <div className="text-white/50 text-[13px] mt-1.5 leading-relaxed">
                  {v.sub}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="container-wide py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <div className="eyebrow">Procesul de verificare</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Cum ajunge o mașină în{" "}
            <span className="font-serif-italic gold-gradient-text font-medium">
              stocul AutoRIO
            </span>
          </h2>
          <p className="mt-4 text-white/55 text-base sm:text-lg">
            Un proces atent, în patru pași, aplicat fiecărui autoturism.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-white/[0.06] bg-[#111111] p-6 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="h-12 w-12 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
                  <s.icon
                    className="h-5 w-5 text-[#D4AF37]"
                    strokeWidth={2.2}
                  />
                </span>
                <span
                  className="font-display text-[42px] font-extrabold leading-none select-none"
                  style={{
                    WebkitTextStroke: "1.5px rgba(212,175,55,0.5)",
                    color: "transparent",
                  }}
                >
                  {s.n}
                </span>
              </div>
              <h3 className="font-display text-lg font-extrabold text-white leading-tight">
                {s.title}
              </h3>
              <p className="mt-2 text-[14px] text-white/55 leading-relaxed">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="container-wide py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="relative rounded-3xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#111] to-[#0a0a0a] p-8 sm:p-12">
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#D4AF37]/12 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 h-48 w-48 rounded-full bg-[#D4AF37]/8 blur-3xl" />
          <div className="relative">
            <div className="eyebrow">De ce clienții aleg AutoRIO</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              Beneficii{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                reale
              </span>
              , nu doar promisiuni
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              {REASONS.map((r, i) => (
                <motion.div
                  key={r.text}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 h-9 w-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                    <r.icon
                      className="h-4 w-4 text-[#D4AF37]"
                      strokeWidth={2.2}
                    />
                  </span>
                  <div>
                    <div className="text-white font-bold text-[15px] leading-tight">
                      {r.text}
                    </div>
                    <div className="text-white/50 text-[13px] mt-1">
                      {r.sub}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-wide pt-12 sm:pt-16">
        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#0e0d0b] to-[#0a0a0a] px-6 py-14 sm:px-12 sm:py-20 text-center">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <div className="eyebrow">Următorul pas</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
              Descoperă autoturismele{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                disponibile
              </span>
            </h2>
            <p className="mt-4 text-white/60 text-base sm:text-lg">
              Vezi stocul actual sau ia legătura direct cu noi pentru o
              recomandare personalizată.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/masini"
                className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]"
              >
                Vezi stocul <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-transparent border border-white/25 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors"
              >
                <Phone className="h-4 w-4" />
                Contactează-ne
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
